import React, { useState, useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import Loading from "react-fullscreen-loading";
import FilterListIcon from "@material-ui/icons/FilterList";
import HelpOutlineIcon from "@material-ui/icons/HelpOutline";
import _ from "lodash";

import Header from "@components/Header";
import { ModalVehicleAndTravelDetails } from "@components/Modal/VehicleAndTravelDetails";
import useTranslation from "src/translations/useTranslation";
import { TravelAnalysisMessages } from "@shared/languages/interfaces";
import { Travel } from "@shared/interfaces/travel.interface";
import api from "@services/api";
import { useToast } from "@hooks/useToast";

import useTravelAnalysis from "./hooks/useTravelAnalysis";
import useTravelAnalysisAlerts from "./hooks/useTravelAnalysisAlerts";
import useTravelAnalysisMapMarkers from "./hooks/useTravelAnalysisMapMarkers";
import useTravelAnalysisGrouped from "./hooks/useTravelAnalysisGrouped";
import KpiCards from "./components/molecules/KpiCards";
import QueryFilterTravelAnalysis from "./components/organisms/QueryFilterTravelAnalysis";
import AlertDistributionChart from "./components/organisms/AlertDistributionChart";
import SuccessRatioChart from "./components/organisms/SuccessRatioChart";
import SuccessGroupedChart from "./components/organisms/SuccessGroupedChart";
import GeographicMonitoring from "./components/organisms/GeographicMonitoring";
import AlertsDrilldownModal from "./components/organisms/AlertsDrilldownModal";
import AlertGlossary from "./components/organisms/AlertGlossary";
import { AlertByType } from "./types";

import {
  Container,
  PageTitleSection,
  TopBar,
  TopBarActions,
  FilterButton,
  HelpButton,
  ChartsRow,
  GroupedChartsRow,
  GroupedChartsPairRow,
  MapSection
} from "./styles";

/** Travel Analysis dashboard page. */
const TravelAnalysis: React.FC = () => {

  const screen = useSelector(({ screen: state }: any) => state);
  const { t } = useTranslation();
  const { addToast } = useToast();
  const [openFilter, setOpenFilter] = useState(false);
  const [openGlossary, setOpenGlossary] = useState(false);
  const { metrics, loading, error, currentFilter, handleFilter } = useTravelAnalysis();
  const drilldown = useTravelAnalysisAlerts(currentFilter);
  const { markers: mapMarkers, markersLoading } = useTravelAnalysisMapMarkers(currentFilter);
  const grouped = useTravelAnalysisGrouped(currentFilter);

  // Travel detail modal state
  const [selectedTravel, setSelectedTravel] = useState<Travel>({} as Travel);
  const [openTravelModal, setOpenTravelModal] = useState(false);
  const [loadingTravel, setLoadingTravel] = useState(false);

  /** Click on a chart bar → open drilldown filtered by that alert type. */
  const handleBarClick = useCallback((alertType: AlertByType) => {
    drilldown.openDrilldown(alertType.id, alertType.description);
  }, [drilldown.openDrilldown]);

  /** Click on the "Alertas Detectados" KPI card → open drilldown with all alerts. */
  const handleAlertsKpiClick = useCallback(() => {
    drilldown.openDrilldown();
  }, [drilldown.openDrilldown]);

  /** Click on an alert type inside a map InfoWindow → open drilldown filtered by type + origin. */
  const handleMapAlertTypeClick = useCallback((alertTypeDescription: string, originName: string) => {
    const found = metrics?.alertsByType?.find((a) => a.description === alertTypeDescription);

    drilldown.openDrilldown(found?.id, alertTypeDescription, { originName });
  }, [drilldown.openDrilldown, metrics]);

  /** Click on a grouped chart bar → open drilldown filtered by the clicked dimension. */
  const handleGroupedDayClick = useCallback((label: string) => {
    drilldown.openDrilldown(undefined, undefined, { dayFilter: label });
  }, [drilldown.openDrilldown]);

  const handleGroupedPlantClick = useCallback((label: string) => {
    drilldown.openDrilldown(undefined, undefined, { originName: label });
  }, [drilldown.openDrilldown]);

  const handleGroupedRegionalClick = useCallback((label: string) => {
    drilldown.openDrilldown(undefined, undefined, { regionalName: label });
  }, [drilldown.openDrilldown]);

  /** Open the standard travel detail modal from a drilldown row. */
  const handleOpenTravel = useCallback(async (travelId: string) => {
    setLoadingTravel(true);
    try {
      const [travelRes, statesRes, alertsRes] = await Promise.all([
        api.get(`travels/read/${travelId}`).then((r) => r.data),
        api.get(`vehicles/states/get-filtered-by-travel/${travelId}`).then((r) => r.data),
        api.post("alerts/read/", { idTravel: travelId }).then((r) => r.data)
      ]);

      const travelDetails = {
        ...travelRes.result,
        vehicle: {
          ...travelRes.result.vehicle,
          states: statesRes.result,
          alerts: alertsRes.result
        }
      };

      setSelectedTravel(travelDetails);
      setOpenTravelModal(true);
    } catch {
      addToast({ type: "error", title: t(TravelAnalysisMessages.noData) });
    } finally {
      setLoadingTravel(false);
    }
  }, [addToast, t]);

  /** Memoized travel detail modal. */
  const travelDetailModal = useMemo(() => {
    if (_.isEmpty(selectedTravel)) return null;

    return (
      <ModalVehicleAndTravelDetails
        vehicle={{ ...selectedTravel.vehicle }}
        travel={selectedTravel}
        open={openTravelModal}
        onClose={() => setOpenTravelModal(false)}
        historical
        overAll
        onlyView
      />
    );
  }, [selectedTravel, openTravelModal]);

  return (
    <>
      <Header title={t(TravelAnalysisMessages.pageTitle)} />
      <Loading loading={loading} />
      <Container platform={screen.platform}>
        <TopBar>
          <PageTitleSection>
            <div className="page-subtitle">{t(TravelAnalysisMessages.dashboardSubtitle)}</div>
            <div className="page-title">{t(TravelAnalysisMessages.pageTitle)}</div>
          </PageTitleSection>
          <TopBarActions>
            <HelpButton onClick={() => setOpenGlossary(true)}>
              <HelpOutlineIcon />
              {t(TravelAnalysisMessages.glossaryButtonLabel)}
            </HelpButton>
            <FilterButton onClick={() => setOpenFilter(true)}>
              <FilterListIcon />
              {t(TravelAnalysisMessages.filterButton)}
            </FilterButton>
          </TopBarActions>
        </TopBar>
        {!loading && (
          <>
            <KpiCards metrics={metrics} loading={loading} platform={screen.platform} onAlertsClick={handleAlertsKpiClick} />
            <ChartsRow platform={screen.platform}>
              <AlertDistributionChart
                alertsByType={metrics?.alertsByType ?? []}
                loading={loading}
                onBarClick={handleBarClick}
              />
              <SuccessRatioChart metrics={metrics} loading={loading} />
            </ChartsRow>
            <GroupedChartsRow platform={screen.platform}>
              <SuccessGroupedChart
                title={t(TravelAnalysisMessages.successByDayTitle)}
                data={grouped.byDay}
                loading={grouped.loading}
                onBarClick={handleGroupedDayClick}
              />
            </GroupedChartsRow>
            <GroupedChartsPairRow platform={screen.platform}>
              <SuccessGroupedChart
                title={t(TravelAnalysisMessages.successByPlantTitle)}
                data={grouped.byPlant}
                loading={grouped.loading}
                onBarClick={handleGroupedPlantClick}
              />
              <SuccessGroupedChart
                title={t(TravelAnalysisMessages.successByRegionalTitle)}
                data={grouped.byRegional}
                loading={grouped.loading}
                onBarClick={handleGroupedRegionalClick}
              />
            </GroupedChartsPairRow>
            <MapSection>
              <GeographicMonitoring
                metrics={metrics}
                loading={loading}
                mapMarkers={mapMarkers}
                markersLoading={markersLoading}
                onAlertTypeClick={handleMapAlertTypeClick}
              />
            </MapSection>
            {error && (
              <div style={{ color: "#ff6c5c", textAlign: "center", padding: 16 }}>
                {error}
              </div>
            )}
          </>
        )}
      </Container>
      <QueryFilterTravelAnalysis
        open={openFilter}
        onClose={() => setOpenFilter(false)}
        onFilter={handleFilter}
      />
      <AlertGlossary open={openGlossary} onClose={() => setOpenGlossary(false)} />
      <AlertsDrilldownModal
        open={drilldown.open}
        onClose={drilldown.closeDrilldown}
        alerts={drilldown.alerts}
        loading={drilldown.alertsLoading}
        totalCount={drilldown.totalCount}
        selectedAlertType={drilldown.selectedAlertType}
        onLoadMore={drilldown.loadMore}
        onOpenTravel={handleOpenTravel}
      />
      <Loading loading={loadingTravel} />
      {openTravelModal && travelDetailModal}
    </>
  );
};

export default TravelAnalysis;
